import React, {useContext, useEffect, useState} from 'react';
import {useHistory} from 'react-router-dom';
import {TableColumn, TableSettings} from "../../../tableSettings";
import AbstractListView, {
  DataFetchResult,ExportRowRenderer,ListCustomFilterRenderer,ListOrderBy,ListRowRenderer
} from "../../common/AbstractListView";
import * as XCol from '../../../framework.core/XExportColumn';
import {Search} from "../../../filtering";
import {NavigationType, openNewPage} from '../../../util/navigationHelpers';
import {
  DateRenderer,DateTimeRenderer,FloatRenderer,NullableCheckboxIconRenderer,
  TextAlignLeftRenderer,TextRenderer
} from '../../../uiControls/SmartTableRenderers';
import UniqueLinkButton from '../../../uiControls/UniqueLinkButton';
import JobListViewContext, { RightClickContext } from '../../common/JobListViewContext';
import useGlobalOkDialog from '../../../hooks/useGlobalOkDialog';
import {JobNames} from "../../../framework.core/JobNames";
import {companyList} from "../tableSettings";
import {WhCustListItem} from "../../../fms.data/models/WhCustListItem";
import ListViewShortcutMenu from '../../common/ListViewShortcutMenu';
import { CTypeFilterRenderer, InactiveFilterRenderer } from '../../common/commonListRenderers';
import { ActionButtonItem, borderColors, splitMenuItemsIntoSections } from '../../../ui/ActionButton';
import Menu, { IfmsMenuItem } from '../../../ui/menu/Menu';
import ToolboxMenuItem from '../../../uiControls/ToolboxMenuItem';
import useFunctionKeys from '../../../hooks/useFunctionKeys';
import ApplicationContext from '../../../ApplicationContext';
import { FmsSecTask } from '../../../fms.core/constants';
import { findSecurityTask } from '../../../util';
import { CustomerTypeValue, nullableCheckBoxValue } from '../../common/commonExportFormat';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { EditIconButton, EditIconButton2, ViewIconButton, ViewIconButton2 } from '../../../ui/IconButton';

function VoidRenderer({cell}:any) {
  if (cell && cell.value === true) {
    return (
      <div data-tooltip={'Void'} className="ifms-table-default">
        <span style={{color: 'red'}}>V</span>
      </div>
    );
  }
  return <div className="ifms-table-default"></div>;
}

function TypeRenderer({cell}:any) {
  if(cell && cell.value === 'C'){
    return (
      <div className="ifms-table-align-left">
        {cell.value}
      </div>
    );
  }
  else if(cell && cell.value === 'ST'){
    return (
      <div className="ifms-table-align-left">
        {'ST'}
      </div>
    );
  }
  else if(cell && cell.value === 'SF'){
    return (
      <div className="ifms-table-align-left">
        {'SF'}
      </div>
    );
  }
  else if(cell && cell.value === 'SFST'){
    return (
      <div className="ifms-table-align-left">
        {'SF+'}
      </div>
    );
  }
  else
  return (
    <div className="ifms-table-align-left">
      {'C+'}
    </div>
  );
}


function useSelectedOperations() {
  const jobListContext = useContext(JobListViewContext);
  const history = useHistory();
  const appContext = useContext(ApplicationContext);

  const selectedElement = jobListContext.selectedElement as WhCustListItem;

  const [showGlobalOkDialog] = useGlobalOkDialog();

  async function create() {
    if (jobListContext.hasOpenedScreenStartingWithUrl('/warehousing/company/add')) {
      await showGlobalOkDialog(<p>New operation is already in progress</p>);
      return;
    }
    openNewPage(history, '/warehousing/company/add', NavigationType.newPage);
  }

  async function edit() {
    if (!selectedElement) {
      return;
    }

    const id = selectedElement.id;
    const editUrl = `/warehousing/company/edit/${id}`;
    const viewUrl = `/warehousing/company/view/${id}`;

    // if (jobListContext.hasOpenedScreenStartingWithUrlWithoutMe('/warehousing/company/edit', editUrl)) {
    //   await showGlobalOkDialog(<p>Edit operation is already in progress</p>);
    //   return;
    // }

    jobListContext.moveToOpenedScreenInAnyOperationStartingWithUrl(editUrl, [editUrl, viewUrl]);
  }

  async function view() {
    if (!selectedElement) {
      return;
    }
    const id = selectedElement.id;
    const editUrl = `/warehousing/company/edit/${id}`;
    const viewUrl = `/warehousing/company/view/${id}`;

    const listSec = findSecurityTask(appContext, FmsSecTask.WHCustomer);
    const urls = [viewUrl];
    if (listSec?.grantEdit) urls.push(editUrl)

    jobListContext.moveToOpenedScreenInAnyOperationStartingWithUrl(viewUrl, urls);
  }
  return { create, edit, view };
}

function RightClickMenu({ dismiss }: { dismiss: VoidFunction }) {
  const { edit, view, create} = useSelectedOperations();
  const jobListContext = useContext(JobListViewContext);
  
  const appContext = useContext(ApplicationContext);
  const listSec = findSecurityTask(appContext, FmsSecTask.WHCustomer);

  const items = [
    { label: 'New', shortcut: 'F2', separator: false, hidden: !listSec?.grantAdd },
    { label: 'View', shortcut: 'F9', separator: false },
    { label: 'Edit', shortcut: 'F3', separator: false, hidden: !listSec?.grantEdit },
    { label: 'Search', shortcut: 'F5', separator: false, newCol: true }
   ];

  const menuItems: ActionButtonItem[] = items.map((i, index) => ({
    label: <ToolboxMenuItem name="whCustomer-list-sub-menu" width="190px" label={i.label} shortcut={i.shortcut} />,
    separator: i.separator,
    selected: false,
    newCol: i.newCol,
    index,
    hidden: i?.hidden ?? false,
  }));

  function onMenuItemSelected(item: IfmsMenuItem) {
    switch (item.index) {
      case 0:
        create();
        break;
      case 1:
        view();
        break;
      case 2:
        edit();
        break;
      case 3:
        jobListContext.triggerTableSearch('F5');
        break;
      case 4:
        jobListContext.setInfoAreaContent(jobListContext.infoAreaContent === 'memo' ? 'none' : 'memo');;
        break;
      case 5:
        jobListContext.reloadData();
        break;
      default:
        break;
    }
    dismiss();
  }

  return (
    <Menu
      variant="plain"
      specialMenu='rightClickContext'
      items={splitMenuItemsIntoSections(menuItems)}
      mode="vertical"
      onMenuItemSelected={onMenuItemSelected}
      subMenuStyle={{
        minWidth: 150,
        whiteSpace: 'nowrap',
      }}
      menuStyle={(i: number) => ({
        maxHeight: '300px',
        overflowY: 'hidden',
        padding: 0,
        borderTop: `3px solid ${borderColors[i % borderColors.length]}`,
        paddingTop: 10,
      })}
      dismiss={dismiss}
    />
  );
}

function ListCommands() {
  const jobListContext = useContext(JobListViewContext);
  const appContext = useContext(ApplicationContext);

  const selectedElement = jobListContext.selectedElement as WhCustListItem;

  const {edit, view,} = useSelectedOperations();

  const listSec = findSecurityTask(appContext, FmsSecTask.WHCustomer);

  const hasSelectedRow = !!selectedElement;
  const canBeAdd = listSec?.grantAdd;
  const canBeEdit = hasSelectedRow && listSec?.grantEdit;

  return (
     <div className='ifms-common-no-vertical-separator-gap-margin'>
      {/* {appContext.suntekSettings?.company?.billingsTabVersion === 2 ?  <div className={!appContext.useDarkMode ? "ifms-common-vertical-separator" : "ifms-common-vertical-separator-darkmode"}/> : <></>} */}
      <div className='ifms-common-vertical-gap-8'>
        <UniqueLinkButton newStyle name='whCustomer-list-add-button' display="primary" link="/warehousing/company/add" disabled={!canBeAdd} label={<FontAwesomeIcon icon={faPlus} />} useOnlyIcon data-tooltip='New' iconMode />
        <EditIconButton2 name='whCustomer-list-edit-button' disabled={!canBeEdit} onClick={edit} useText/>
        <ViewIconButton2 name='whCustomer-list-view-button' disabled={!hasSelectedRow} onClick={view} useText />
      </div>
    </div>
  );
}

function ListToolbar() {
  return (
    <>
      <ListViewShortcutMenu />
    </>
  );
}

function ListViewExtras() {
  useFunctionKeys({onFunction: onFunctionKey});
  const jobListContext = useContext(JobListViewContext);
  const appContext = useContext(ApplicationContext);
  const listSec = findSecurityTask(appContext, FmsSecTask.WHCustomer);
  const {create, edit, view} = useSelectedOperations();
  const [unprocessedFkey, setUnprocessedFkey] = useState<string | null>(null);

  useEffect(() => {
      if (!unprocessedFkey) {
        return;
      }
      doProcessFkey();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [unprocessedFkey]
  );

  function doProcessFkey() {
    if (unprocessedFkey === "F2") {
      if(listSec?.grantAdd)
        create();
    } else if (unprocessedFkey === "F3") {
      if(listSec?.grantEdit)
        edit();
    } else if (unprocessedFkey === "F9") {
      view();
    } else if (unprocessedFkey === "F11") {
      jobListContext.reloadData();
    } 
    setUnprocessedFkey(null);
  }

  function onFunctionKey(fkey: string) {
    if (['F2', 'F3', 'F9', 'F11'].indexOf(fkey) >= 0) {
      setUnprocessedFkey(fkey);
      return true;
    }

    return false;
  }
  return (
    <></>
  );
}

/**
 * Concrete implementation for Other Shipment List screen
 *
 * @class ListView
 * @extends {AbstractListView}
 */
class ListView extends AbstractListView {
  getTableSettings(): TableSettings {
    const tsettings =  companyList;
    const cols = WhCustListItem.getExportCollection();
    tsettings.columns = cols.map<TableColumn>(x => {
      return { ...x, accessor: x.dataIndex, Header: x.text, filterType: undefined, filterTypeV2: x.type};
    });
    return tsettings;
  }

  getCustomFilterRenderers(): ListCustomFilterRenderer[] {
    return [
      {
        accessor: 'void',
        renderer: InactiveFilterRenderer,
        searchValueExtractor: (v:any|null|undefined) => v === 'V' ? true : false,
      },
      {
        accessor: 'type',
        renderer: CTypeFilterRenderer,
        searchValueExtractor: (v:any|null|undefined) => v ,
      },
    ]
  }

  getRowRenderers(): ListRowRenderer[] {
    return WhCustListItem.getExportCollection().map(x => {
      const colRenderer:ListRowRenderer = { accessor: x.dataIndex, Cell: TextAlignLeftRenderer };
      if(x.align === XCol.XAlignType.Center) colRenderer.Cell = TextRenderer;
      if(x.dataIndex === "companyId") colRenderer.Cell = TextAlignLeftRenderer;
      if(x.dataIndex === "type") colRenderer.Cell = TypeRenderer;
      else if(x.dataIndex === "status") {
        colRenderer.Cell = () => <span></span>
        colRenderer.Header = () => <span></span>;
      }
      else if(x.type === XCol.XCellType.Bool || x.type === XCol.XCellType.StrBool) colRenderer.Cell = NullableCheckboxIconRenderer;
      else if(x.type === XCol.XCellType.Date) colRenderer.Cell = DateRenderer;
      else if(x.type === XCol.XCellType.DateTime) colRenderer.Cell = DateTimeRenderer;
      else if(x.type === XCol.XCellType.Double) colRenderer.Cell = (props: any) => <FloatRenderer {...props} decimals={2} />;
      if (x.dataIndex === 'void') {
        colRenderer.Cell = VoidRenderer;
        colRenderer.Header = () => <span>Inactive</span>;
      }
      return colRenderer;
    });
  }
  
  getExportRenderers(): ExportRowRenderer[] {
    const colRenderer = [];
    colRenderer.push({accessor: 'status', changedValue: (value: any)=> { return value['status'] === 'PC' ? 'Period Close' : ''}});
    colRenderer.push({accessor: 'type', changedValue: (value: any)=> { return CustomerTypeValue(value['type'])}});
    colRenderer.push({accessor: 'type', changedValue: (value: any)=> { return nullableCheckBoxValue(value['void'])}});
return colRenderer;
  }

  getDefaultOrderBy(): ListOrderBy {
    return {
      orderBy: 'u2Date',
      orderByAscending: false,
    };
  }

  async fetchAndFormatData(api: any, search: Search, pageNum: number, limit: number): Promise<DataFetchResult> {
    const apiResult = await api.getListItems(
      JobNames.WHCustomer,
      'WhCustListItem',
      WhCustListItem.calculateSearchParameters(search, WhCustListItem.MapSearchFields,['u2Date']),
      pageNum,
      limit,
      search.pageSize,
      search.pagePerBatch,
      );

    if (!apiResult || !apiResult.entities || apiResult.entities.length <= 0) {
      return {data: [], totalCount: 0};
    }

    return {data: WhCustListItem.formatData(apiResult.entities), totalCount: apiResult.totalCount};
  }

  async exportFormatDataWithExcel(api: any, search: Search, pageNum: number, limit: number, pageSize: number, pagePerBatch: number): Promise<Array<any>> {
    const apiResult = await api.getListItems(JobNames.WHCustomer, 'WhCustListItem',
      WhCustListItem.calculateSearchParameters(search, WhCustListItem.MapSearchFields,['u2Date']),
      pageNum,
      limit,
      pageSize,
      pagePerBatch,
    );

    if (!apiResult || !apiResult.entities || apiResult.entities.length <= 0) {
      return [];
    }

    return WhCustListItem.formatData(apiResult.entities);
  }

  renderCommands() {
    return (
      <ListCommands/>
    );
  }

  renderBeforeConfigureButton() {
    return (
      <ListToolbar/>
    );
  }

  renderExtras() {
    return <ListViewExtras/>
  }

  getViewUrlFor(row: WhCustListItem) {
    return row && row.id ? `/warehousing/company/view/${row.id}` : '';
  }

  getListViewTitle() {
    return 'Customer List';
  };

  hasRightClickContext(): boolean {
    return true;
  }
  
  renderRightClickContext(context: RightClickContext, dismiss: VoidFunction): React.ReactNode {
    return <RightClickMenu dismiss={dismiss} />;
  }

  hasMemo(): boolean {
    return false;
  }
}

export default ListView;
